'use client';

import { useState } from 'react';

import { SubscriptionLevelBadge } from '@ps/features/subscription/components/SubscriptionLevelBadge';
import { PopStoreExtendedSubscriptionLevel } from '@ps/features/subscription/types/SubscriptionLevel';
import { Dialog, DialogContent, DialogTitle } from '@ps/ui/atoms/dialog';
import { Bookmark, Flag, Pencil, Trash2, Users } from 'lucide-react';

import { useChannelReports } from '../../hooks/useChannelReports';
import { useMyChannelBookmarks } from '../../hooks/useMyChannelBookmarks';
import { useMyChannelMembership } from '../../hooks/useMyChannelMembership';
import { ChannelVisibility } from '../../types/ChannelVisibility';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelBookmarksSheet } from '../channel-info/ChannelBookmarksSheet';
import { ChannelMembersSheet } from '../channel-info/ChannelMembersSheet';
import { ChannelReportsSheet } from '../channel-info/ChannelReportsSheet';
import { DeleteChannelDialog } from '../channel-info/DeleteChannelDialog';
import { EditChannelModal } from '../channel-info/EditChannelModal';
import { ChannelAvatar } from '../common/ChannelAvatar';
import {
  ChannelInfoRow,
  ChannelInfoRowButton,
  RowCount,
} from './ChannelInfoRows';
import { ChannelNotificationsControl } from './ChannelNotificationsControl';

interface Props {
  channel: CommunityChannel;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

type OpenSheet = 'members' | 'reports' | 'bookmarks' | 'edit' | null;

/**
 * Channel info popover — opens from the channel header kebab.
 *
 * Slice 3 stood up the toggle controls and the Delete confirmation;
 * slice 4 fills in the sub-panel actions: Edit Channel opens a
 * modal, and Members / Reports / Bookmarks each open a right-aligned
 * sheet. The popover dismisses while a sub-panel is showing so the
 * user can focus on the panel content; closing the sub-panel
 * doesn't reopen the popover — the user can re-tap the kebab if
 * they want to come back.
 */
export function ChannelInfoPopover({ channel, open, onOpenChange }: Props) {
  const member = useMyChannelMembership(channel.id);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);

  // Row counts mirror upstream (bookmark/report `.length`). Subscribe only
  // while the popover is open — both hooks bail on a null channelId — so a
  // closed popover doesn't hold live Firestore listeners open per channel.
  const reports = useChannelReports(open ? channel.id : undefined);
  const bookmarks = useMyChannelBookmarks(open ? channel.id : undefined);
  const reportCount = reports.reports.length;
  const bookmarkCount = bookmarks.bookmarks.length;

  const isPrivate = channel.visibility === ChannelVisibility.Private;

  const openSubPanel = (panel: Exclude<OpenSheet, null>) => {
    onOpenChange(false);
    setOpenSheet(panel);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* `max-h-[90dvh]` + `overflow-hidden` bounds the FRAME, and the inner
            wrapper below does the scrolling. This panel had neither, so the
            owner's longer row set (Edit / Members / Reports / Bookmarks /
            Notifications / Type / Subscription / Delete) simply overflowed the
            viewport off-screen with nothing to scroll. `p-0` moves the padding
            onto the scroller so it doesn't clip; the built-in close button stays
            pinned to the frame rather than scrolling away with the content. */}
        <DialogContent
          showCloseButton
          className="text-content-1 border-line bg-surface-1 flex max-h-[90dvh] flex-col overflow-hidden p-0 sm:max-w-md"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
            <div className="flex flex-col items-center gap-2 pt-2">
              <ChannelAvatar src={channel.icon} name={channel.name} size={64} />
              <DialogTitle className="text-content-1 text-center text-lg font-semibold">
                {channel.name}
              </DialogTitle>
              <p className="text-content-3 text-sm">
                {channel.messageCount} Posts · {channel.memberCount} Members
              </p>
              {channel.description ? (
                <p className="text-content-2 text-center text-sm">
                  {channel.description}
                </p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => openSubPanel('edit')}
              className="border-brand text-brand-text hover:bg-brand/10 mt-3 inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium"
            >
              <Pencil className="h-4 w-4" aria-hidden /> Edit Channel Info
            </button>

            <div className="divide-line border-line mt-2 divide-y rounded-md border">
              <ChannelInfoRowButton
                icon={<Users className="h-4 w-4" aria-hidden />}
                label="Members"
                right={<RowCount value={channel.memberCount} />}
                onClick={() => openSubPanel('members')}
              />
              <ChannelInfoRowButton
                icon={<Flag className="h-4 w-4" aria-hidden />}
                label="Reports"
                right={<RowCount value={reportCount} />}
                onClick={() => openSubPanel('reports')}
              />
              <ChannelInfoRowButton
                icon={<Bookmark className="h-4 w-4" aria-hidden />}
                label="Bookmarks"
                right={<RowCount value={bookmarkCount} />}
                onClick={() => openSubPanel('bookmarks')}
              />
              <ChannelNotificationsControl
                channelId={channel.id}
                member={member}
              />
              <ChannelInfoRow
                label="Channel Type"
                right={
                  <span className="text-content-1 text-sm">
                    {isPrivate ? 'Private' : 'Public'}
                  </span>
                }
              />
              <ChannelInfoRow
                label="Required Subscription"
                right={
                  channel.minimumSubscriptionLevel === 'follower' ? (
                    <span className="text-content-2 text-sm">Follower</span>
                  ) : (
                    <SubscriptionLevelBadge
                      level={
                        channel.minimumSubscriptionLevel as PopStoreExtendedSubscriptionLevel
                      }
                    />
                  )
                }
              />
            </div>

            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-red-400 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Delete Channel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteChannelDialog
        channel={channel}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={() => onOpenChange(false)}
      />
      <EditChannelModal
        channel={channel}
        open={openSheet === 'edit'}
        onOpenChange={next => setOpenSheet(next ? 'edit' : null)}
      />
      <ChannelMembersSheet
        channel={channel}
        open={openSheet === 'members'}
        onOpenChange={next => setOpenSheet(next ? 'members' : null)}
      />
      <ChannelReportsSheet
        channel={channel}
        open={openSheet === 'reports'}
        onOpenChange={next => setOpenSheet(next ? 'reports' : null)}
      />
      <ChannelBookmarksSheet
        channel={channel}
        open={openSheet === 'bookmarks'}
        onOpenChange={next => setOpenSheet(next ? 'bookmarks' : null)}
      />
    </>
  );
}
